import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../Services/Firebase/FirebaseIndex";
import { loginUser, signInWithGoogle, initializeGIS } from "../../Services/Firebase/FirebaseModules/firebaseAuthService";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { AnalyticsService } from "../../Services/Firebase/FirebaseModules/firebaseAnalyticsService";
import { ErrorBoundary } from "react-error-boundary";
import "../../global.css";
import googleButtonImageSI from "../../assets/GoogleButtons/Web (mobile + desktop)/png@4x/light/web_light_sq_SI@4x.png";
import gigifyLogo from "../../assets/Logo/GIGiFY_Logo_Vertical_no_tagline.png";
import heroImage from "../../assets/Images/HeroImage900px.png";
// Import background images
import signupBackground from "../../assets/Backgrounds/Signuploginbackground.jpg";
import metalGreyBackground from "../../assets/Backgrounds/MetalGreybackground2000px.jpg";
import DashboardMobileBackground from "../../assets/Backgrounds/DashboardMobileBackground.jpg";
import { useLoading } from '../../Components/GlobalLoading/LoadingContext';
import { useAuth } from '../../Components/Auth/AuthContext';
import debug from '../../utils/debug';
import { Eye, EyeOff } from 'lucide-react';

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
  useEffect(() => {
    debug.error('ErrorFallback', 'Login error captured', {
      error: error.message,
      stack: error.stack
    });
    
    Promise.resolve().then(() => {
      AnalyticsService.errorTracking.logError('login_error', {
        error: error.message,
        stack: error.stack
      });
    });
  }, [error]);

  return (
    <div style={{
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "0.75rem",
      borderRadius: "15px",
      margin: "0.75rem 0",
      textAlign: "center"
    }}>
      <h3 style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>Something went wrong</h3>
      <p style={{ fontSize: "0.75rem", marginBottom: "1rem" }}>{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        style={{
          backgroundColor: "#01a2f1",
          color: "white",
          border: "none",
          padding: "0.5rem 0.75rem",
          borderRadius: "15px",
          cursor: "pointer",
          fontSize: "0.75rem",
          minWidth: "44px",
          minHeight: "44px"
        }}
      >
        Try again
      </button>
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setIsLoading } = useLoading();
  const { initializeAuth } = useAuth();
  
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Check for verification success message
  useEffect(() => {
    const isVerified = searchParams.get('verified');
    if (isVerified === 'true') {
      setSuccess('🎉 Email verification successful! Please log in to continue.');
      // Clear the URL parameter after showing the message
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('verified');
      navigate(`/login?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);
  
  // Mobile state management and scaling
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1000);
  const [isTrueMobile, setIsTrueMobile] = useState(window.innerWidth <= 500);
  const [scaleFactor, setScaleFactor] = useState(1);

  // Calculate scaling factor based on viewport width
  useEffect(() => {
    const calculateScale = () => {
      const width = window.innerWidth;
      if (width <= 1000) {
        setScaleFactor(1); // Mobile layout, no scaling needed
      } else if (width >= 1900) {
        setScaleFactor(1); // Base size at 1900px and above (100%)
      } else {
        // Linear scaling between 1000px (80%) and 1900px (100%)
        // At 1000px: scale = 0.8, at 1900px: scale = 1.0
        const scale = 0.8 + (width - 1000) * 0.2 / (1900 - 1000);
        setScaleFactor(scale);
      }
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  // Hide footer on mount and show it when unmounting
  useEffect(() => {
    // Hide any footer elements
    const footers = document.querySelectorAll('footer, [class*="footer"], [id*="footer"]');
    const headers = document.querySelectorAll('header, [class*="header"], [id*="header"]');
    const navbars = document.querySelectorAll('nav, [class*="navbar"], [class*="nav"]');
    
    footers.forEach(footer => {
      footer.style.display = 'none';
    });
    headers.forEach(header => {
      header.style.display = 'none';
    });
    navbars.forEach(navbar => {
      navbar.style.display = 'none';
    });
    
    // Set body styles to prevent scrolling and ensure full coverage
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.height = '100vh';
    document.body.style.width = '100vw';
    
    return () => {
      // Restore footer, header, and navbar visibility when component unmounts
      footers.forEach(footer => {
        footer.style.display = '';
      });
      headers.forEach(header => {
        header.style.display = '';
      });
      navbars.forEach(navbar => {
        navbar.style.display = '';
      });
      
      // Restore body styles
      document.body.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.height = '';
      document.body.style.width = '';
    };
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1000);
      setIsTrueMobile(window.innerWidth <= 500);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to scale values for desktop
  const scaleValue = useCallback((baseValue, unit = 'px') => {
    if (isMobile) return baseValue + unit; // Don't scale mobile values
    const numValue = parseFloat(baseValue);
    return (numValue * scaleFactor) + unit;
  }, [scaleFactor, isMobile]);

  // Helper function to scale rem values
  const scaleRem = useCallback((baseRem) => {
    if (isMobile) return baseRem + 'rem'; // Don't scale mobile values
    const numValue = parseFloat(baseRem);
    return (numValue * scaleFactor) + 'rem';
  }, [scaleFactor, isMobile]);

  // Memoized event handlers to prevent re-renders
  const handleInputChange = useCallback((setter) => (e) => {
    setter(e.target.value);
  }, []);

  // Component lifecycle logging
  useEffect(() => {
    debug.component.mount('Login');
    
    Promise.resolve().then(() => {
      AnalyticsService.trackScreenView('login_page');
    });
    
    initializeGIS().catch(error => {
      debug.error('Login', 'Failed to initialize Google Identity Services', error);
    });
    
    return () => {
      debug.component.unmount('Login');
    };
  }, []);

  // Handle login with improved error handling
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    debug.log('Login', 'Login attempt started', { email: loginEmail });
    
    Promise.resolve().then(() => {
      AnalyticsService.logCustomEvent('login_attempt', {
        method: 'email'
      });
    });
    
    try {
      await initializeAuth();
      
      const loginResult = await loginUser(loginEmail, loginPassword);
      
      if (!loginResult || !loginResult.user) {
        debug.error('Login', 'Login failed - no user returned');
        throw new Error("Login failed: No user returned from authentication");
      }
      
      const user = loginResult.user;
      const userType = loginResult.userType || "Talent";
      const isVerified = loginResult.isVerified;
      
      debug.firebase('Login', "User authenticated", { 
        userUID: user.uid,
        isVerified: isVerified
      });
      
      if (!isVerified) {
        debug.log('Login', "User might not be verified, checking current status");
        
        await user.reload();
        
        if (user.emailVerified) {
          debug.log('Login', "User is verified after reload, skipping verification page");
        } else {
          debug.log('Login', "User confirmed not verified, redirecting to verification page");
          setIsLoading(true);
          navigate(`/verify-email?userUID=${user.uid}&type=${userType}`);
          return;
        }
      }
      
      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('login_success', {
          method: 'email',
          userType: userType
        });
      });
      
      // Check for redirect parameter from verification
      const redirectPath = searchParams.get('redirect');
      const destination = redirectPath || `/${userType.toLowerCase()}-dashboard/${user.uid}`;
      
      debug.log('Login', `Login successful, redirecting to ${destination}`, { redirectPath });
      navigate(destination);
      
    } catch (error) {
      debug.error('Login', "Login error", error);
      let errorMessage = "Login failed";
      
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage = "Invalid email format";
            break;
          case 'auth/user-not-found':
            errorMessage = "No account found with this email";
            break;
          case 'auth/wrong-password':
            errorMessage = "Incorrect password";
            break;
          case 'auth/too-many-requests':
            errorMessage = "Too many failed login attempts. Please try again later";
            break;
          default:
            errorMessage = error.message || "Login failed. Please try again";
        }
      } else {
        errorMessage = error.message || "Login failed. Please try again";
      }
      
      setError(errorMessage);
      
      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('login_failure', {
          method: 'email',
          reason: errorMessage
        });
      });
    } finally {
      setIsLoading(false);
    }
  }, [initializeAuth, loginEmail, loginPassword, navigate, setIsLoading]);

  const handleGoogleAuth = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    
    debug.log('Login', 'Google authentication attempt started');
    
    Promise.resolve().then(() => {
      AnalyticsService.logCustomEvent('auth_attempt', {
        method: 'google'
      });
    });
    
    const target = e.currentTarget;
    if (target) {
      target.style.opacity = "0.7";
    }
    
    setIsLoading(true);
    
    try {
      if (!auth) {
        console.error('Firebase auth not available, initializing');
        await initializeAuth();
      }
      
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',
        access_type: 'offline'
      });
      
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      
      if (!user) {
        console.error('Authentication failed: No user returned');
        throw new Error("Google authentication failed");
      }
      
      const userDoc = await getDoc(doc(db, "Users", user.uid));
      let isNewUser = !userDoc.exists();
      let userType = "Talent"; // Default for login page
      
      if (userDoc.exists()) {
        debug.log('Login', 'Existing user found in Firestore');
        userType = userDoc.data()?.profile?.userType || "Talent";
      } else {
        debug.log('Login', 'Creating new user profile for Google sign-in');
        
        const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        const userData = {
          profile: {
            firstName: firstName,
            lastName: lastName,
            email: user.email,
            userType: userType,
            photoURL: user.photoURL || '',
            isVerified: user.emailVerified,
            dateCreated: serverTimestamp(),
            lastUpdated: serverTimestamp()
          }
        };
        
        await setDoc(doc(db, "Users", user.uid), userData);
        debug.log('Login', 'New user profile created');
      }
      
      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('auth_success', {
          method: 'google',
          userType: userType,
          isExistingUser: !isNewUser
        });
      });
      
      debug.log('Login', "Google auth success", {
        userUID: user.uid,
        userType: userType,
        isVerified: user.emailVerified
      });
      
      if (!user.emailVerified) {
        debug.log('Login', "User not verified, redirecting to verification page", {
          emailVerified: user.emailVerified,
          provider: user.providerData?.[0]?.providerId
        });
        
        if (user.providerData?.[0]?.providerId === 'google.com') {
          await user.reload();
          
          if (user.emailVerified) {
            debug.log('Login', "Google user is verified after reload, skipping verification page");
          } else {
            setIsLoading(true);
            navigate(`/verify-email?userUID=${user.uid}&type=${userType}`);
            return;
          }
        } else {
          setIsLoading(true);
          navigate(`/verify-email?userUID=${user.uid}&type=${userType}`);
          return;
        }
      }
      
      if (isNewUser) {
        debug.log('Login', "New user, navigating to blueprint");
        const destination = `/${userType.toLowerCase()}-blueprint/${user.uid}`;
        navigate(destination, { replace: true });
      } else {
        // Check for redirect parameter from verification
        const redirectPath = searchParams.get('redirect');
        const destination = redirectPath || `/${userType.toLowerCase()}-dashboard/${user.uid}`;
        
        debug.log('Login', "Existing user, navigating to destination", { destination, redirectPath });
        navigate(destination);
      }
      
    } catch (error) {
      console.error('Google Auth Error:', error);
      let errorMessage = "Authentication failed";
      
      if (error.code === "auth/popup-closed-by-user") {
        errorMessage = "Google sign-in was cancelled. Please try again";
      } else if (error.code === "auth/popup-blocked") {
        errorMessage = "Pop-up blocked by browser. Please allow pop-ups for this site.";
      } else if (error.code) {
        errorMessage = error.message || "Authentication failed. Please try again";
      } else {
        errorMessage = error.message || "Authentication failed. Please try again";
      }
      
      setError(errorMessage);
      
      Promise.resolve().then(() => {
        AnalyticsService.logCustomEvent('auth_failure', {
          method: 'google',
          reason: errorMessage,
          errorCode: error.code || 'unknown'
        });
      });
    } finally {
      if (target) {
        target.style.opacity = "1";
      }
      setIsLoading(false);
    }
  }, [initializeAuth, navigate, setIsLoading]);

  // Fixed and enhanced styles for split screen (containers swapped)
  const containerStyle = useMemo(() => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    overflow: "hidden",
    backgroundImage: "none", // No background image for container
    backgroundSize: "cover",
    backgroundPosition: "center"
  }), [isMobile]);

  // Left Panel - Login Form with Metal Grey Background (swapped)
  const leftPanelStyle = useMemo(() => ({
    width: isMobile ? "100%" : "55%",
    height: "100vh",
    backgroundImage: `url(${metalGreyBackground})`, // Same dark background for both mobile and desktop
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "1rem" : scaleRem(2.5), // Scaled for desktop
    paddingTop: isMobile ? "20px" : scaleRem(2.5), // Scaled for desktop
    paddingBottom: isMobile ? "80px" : scaleRem(2.5), // Scaled for desktop
    position: "relative",
    overflowY: isMobile ? "auto" : "hidden"
  }), [isMobile, metalGreyBackground]);

  // Right Panel - Hero Image with Signup Background (swapped)
  const rightPanelStyle = useMemo(() => ({
    width: isMobile ? "100%" : "45%",
    height: "100vh",
    backgroundImage: `url(${signupBackground})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    position: "relative"
  }), [isMobile, signupBackground]);

  const heroImageStyle = useMemo(() => ({
    maxWidth: "130%",
    height: "auto",
    maxHeight: isMobile ? "400px" : scaleValue(800),
    objectFit: "contain",
    filter: `
      drop-shadow(0 ${scaleValue(15)} ${scaleValue(30)} rgba(0,0,0,0.4))
      drop-shadow(0 ${scaleValue(30)} ${scaleValue(60)} rgba(0,0,0,0.2))
      contrast(1.1)
      saturate(1.15)
      brightness(1.05)
      sharpen(0.3)
    `,
    imageRendering: "high-quality",
    transform: "translateZ(0)",
    backfaceVisibility: "hidden",
    perspective: "1000px",
    transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    marginTop: isMobile ? "3rem" : scaleRem(3), // Scaled for desktop
    zIndex: 2,
    willChange: "transform, filter"
  }), [isMobile, scaleValue, scaleRem]);

  const loginContainerStyle = useMemo(() => ({
    backgroundColor: "rgba(255, 255, 255, 0)", // Transparent for both mobile and desktop
    backgroundImage: "none", // No background image for both
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    borderRadius: isMobile ? "15px" : scaleValue(15), // Scaled for desktop
    padding: isMobile ? "0.75rem" : `${scaleRem(2)} ${scaleRem(2.5)}`, // Scaled for desktop
    width: "100%",
    maxWidth: isMobile ? "85%" : scaleValue(715), // Scaled for desktop
    height: isMobile ? "auto" : "auto",
    maxHeight: isMobile ? "none" : "110vh", // Increased height for all non-mobile viewports to accommodate scaled form
    overflowY: isMobile ? "visible" : "auto",
    margin: isMobile ? "auto" : "0",
    boxShadow: isMobile ? `
      0 26px 52px rgba(0, 0, 0, 0.5),
      0 52px 104px rgba(0, 0, 0, 0.4),
      0 78px 156px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.0)
    ` : `
      0 ${scaleValue(26)} ${scaleValue(52)} rgba(0, 0, 0, 0.5),
      0 ${scaleValue(52)} ${scaleValue(104)} rgba(0, 0, 0, 0.4),
      0 ${scaleValue(78)} ${scaleValue(156)} rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.0)
    `,
    backdropFilter: isMobile ? "blur(19.5px)" : `blur(${scaleValue(19.5)})`, // Scaled for desktop
    WebkitBackdropFilter: isMobile ? "blur(19.5px)" : `blur(${scaleValue(19.5)})`, // Scaled for desktop
    transform: isMobile ? "none" : "scale(0.65)", // 35% reduction for non-mobile viewports
    transformOrigin: "center center", // Keep centered when scaling
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden"
  }), [isMobile, scaleValue, scaleRem]);

  const logoContainerStyle = useMemo(() => ({
    marginTop: isMobile ? "0" : scaleRem(0.5), // Scaled for desktop
    marginBottom: isMobile ? (isTrueMobile ? "0.7rem" : "1rem") : "0rem", // Smaller for true mobile
    width: isMobile ? (isTrueMobile ? "120px" : "140px") : scaleValue(250), // Smaller for true mobile
    display: "block",
    margin: isMobile ? (isTrueMobile ? "0 auto 0.7rem auto" : "0 auto 1rem auto") : `${scaleRem(0.5)} auto 0rem auto` // Smaller margins for true mobile
  }), [isMobile, isTrueMobile]);

  const formStyle = useMemo(() => ({
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? (isTrueMobile ? "0.4rem" : "0.5rem") : scaleRem(0.8), // Smaller gap for true mobile
    width: "100%",
  }), [isMobile, isTrueMobile]);

  const inputStyle = useMemo(() => ({
    padding: isMobile ? (isTrueMobile ? "0.6rem" : "0.75rem") : scaleRem(0.975), // Smaller padding for true mobile
    borderRadius: isMobile ? "15px" : scaleValue(15),
    border: isMobile ? "2px solid #dadee6" : `${scaleValue(2.6)} solid #dadee6`, // Scaled for desktop
    width: "100%",
    fontSize: isMobile ? (isTrueMobile ? "0.8rem" : "0.9rem") : scaleRem(1.43), // Smaller font for true mobile
    boxSizing: "border-box",
    transition: "border-color 0.3s ease",
    backgroundColor: "transparent",
    boxShadow: isMobile ? "0 2px 4px rgba(0, 0, 0, 0.05)" : `0 ${scaleValue(2.6)} ${scaleValue(5.2)} rgba(0, 0, 0, 0.05)` // Scaled for desktop
  }), [isMobile, isTrueMobile]);

  const submitButtonStyle = useMemo(() => ({
    padding: isMobile ? (isTrueMobile ? "0.6rem" : "0.75rem") : scaleRem(0.975), // Smaller padding for true mobile
    borderRadius: isMobile ? "15px" : scaleValue(15),
    backgroundColor: "#01a2f1",
    color: "#fff",
    border: "none",
    fontSize: isMobile ? (isTrueMobile ? "0.95rem" : "1.1rem") : scaleRem(1.43), // Smaller font for true mobile
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontWeight: "500",
    position: "relative",
    overflow: "hidden",
    boxShadow: isMobile ? "0 2px 4px rgba(0, 0, 0, 0.2)" : `0 ${scaleValue(2)} ${scaleValue(4)} rgba(0, 0, 0, 0.2)`,
  }), [isMobile, isTrueMobile]);

  const errorMessageStyle = useMemo(() => ({
    color: "#f85008",
    padding: isMobile ? (isTrueMobile ? "0.6rem" : "0.75rem") : scaleRem(0.975), // Smaller padding for true mobile
    borderRadius: isMobile ? "15px" : scaleValue(15),
    marginBottom: isMobile ? (isTrueMobile ? "0.4rem" : "0.5rem") : scaleRem(0.65), // Smaller margin for true mobile
    fontSize: isMobile ? (isTrueMobile ? "0.95rem" : "1.1rem") : scaleRem(1.43), // Smaller font for true mobile
    textAlign: "center",
  }), [isMobile, isTrueMobile]);

  const googleButtonStyle = useMemo(() => ({
    marginTop: isMobile ? (isTrueMobile ? "0.4rem" : "0.5rem") : scaleRem(0.5), // Smaller margin for true mobile
    cursor: "pointer",
    width: isMobile ? (isTrueMobile ? "200px" : "220px") : scaleValue(312), // Smaller for true mobile
    height: "auto",
    transition: "transform 0.2s ease",
    display: "block",
    margin: isMobile ? (isTrueMobile ? "0.4rem auto" : "0.5rem auto") : `${scaleRem(0.5)} auto 0 auto`, // Smaller margins for true mobile
    maxWidth: "90%"
  }), [isMobile, isTrueMobile]);

  // Style for the "Or get started with" divider
  const dividerStyle = useMemo(() => ({
    display: "flex",
    alignItems: "center",
    margin: isMobile ? (isTrueMobile ? "0.4rem 0" : "0.5rem 0") : `${scaleRem(0.8)} 0`, // Smaller margin for true mobile
    color: "#f8f9fa",
    fontSize: isMobile ? (isTrueMobile ? "0.7rem" : "0.8rem") : scaleRem(1.3) // Smaller font for true mobile
  }), [isMobile, isTrueMobile]);

  const dividerLineStyle = useMemo(() => ({
    flex: 1,
    height: "1px",
    backgroundColor: "#f8f9fa",
    margin: "0 0.75rem"
  }), []);

  // Updated marketing text container styles for existing users
  const marketingContainerStyles = useMemo(() => ({
    // Main heading container - at the top
    mainHeading: {
      position: "absolute",
      top: "5%",
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      color: "#f8f9fa",
      fontFamily: "Pondar, sans-serif",
      fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
      fontWeight: "bold",
      textShadow: "0 2px 4px rgba(0,0,0,0.5)",
      zIndex: 2,
      width: "100%"
    },
    subtitle: {
      position: "absolute",
      top: "18%", // Increased from 13% to create more space
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      color: "#f8f9fa",
      fontSize: "clamp(0.8rem, 2vw, 1rem)",
      textShadow: "0 2px 4px rgba(0,0,0,0.5)",
      maxWidth: "100%",
      zIndex: 2
    },
    // Dashboard container
    dashboardContainer: {
      position: "absolute",
      top: "calc(18% - 4rem)", // Moved another 2rem further up (total 4rem)
      left: "2%", // Positioned closer to left edge
      width: "32%", // Slightly narrower for better spacing
      padding: "1.2rem",
      background: "linear-gradient(135deg, rgba(20, 72, 116, 0.12), rgba(20, 72, 116, 0.28), rgba(30, 82, 126, 0.18))",
      borderRadius: "20px",
      color: "#f8f9fa",
      fontSize: "clamp(0.7rem, 1.8vw, 0.9rem)",
      textShadow: "0 3px 12px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)",
      backdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      WebkitBackdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      border: "1px solid rgba(255,255,255,0.4)",
      boxShadow: `
        0 25px 60px rgba(0,0,0,0.7),
        0 12px 30px rgba(0,0,0,0.5),
        0 6px 15px rgba(0,0,0,0.4),
        0 2px 8px rgba(0,0,0,0.3),
        inset 0 3px 0 rgba(255,255,255,0.4),
        inset 0 -3px 0 rgba(0,0,0,0.2),
        inset 2px 0 0 rgba(255,255,255,0.25),
        inset -2px 0 0 rgba(0,0,0,0.15)
      `,
      filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.6))",
      transform: "translateZ(0) perspective(1000px) translateY(-8px)",
      zIndex: 5,
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    },
    // Feature container
    featureContainer: {
      position: "absolute",
      bottom: "8%", // Moved further down for more spacing
      left: "5%", // Moved further left
      width: "28%", // Slightly narrower
      padding: "1.2rem",
      background: "linear-gradient(135deg, rgba(248, 80, 8, 0.12), rgba(248, 80, 8, 0.28), rgba(258, 90, 18, 0.18))",
      borderRadius: "20px",
      color: "#f8f9fa",
      fontSize: "clamp(0.7rem, 1.8vw, 0.9rem)",
      textShadow: "0 3px 12px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)",
      backdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      WebkitBackdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      border: "1px solid rgba(255,255,255,0.4)",
      boxShadow: `
        0 25px 60px rgba(0,0,0,0.7),
        0 12px 30px rgba(0,0,0,0.5),
        0 6px 15px rgba(0,0,0,0.4),
        0 2px 8px rgba(0,0,0,0.3),
        inset 0 3px 0 rgba(255,255,255,0.4),
        inset 0 -3px 0 rgba(0,0,0,0.2),
        inset 2px 0 0 rgba(255,255,255,0.25),
        inset -2px 0 0 rgba(0,0,0,0.15)
      `,
      filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.6))",
      transform: "translateZ(0) perspective(1000px) translateY(-8px)",
      zIndex: 5,
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    },
    // Success container
    successContainer: {
      position: "absolute",
      bottom: "30%", // Moved down for better spacing
      right: "5%", // Moved further right
      width: "28%", // Slightly narrower
      padding: "1.2rem",
      background: "linear-gradient(135deg, rgba(1, 162, 241, 0.12), rgba(1, 162, 241, 0.28), rgba(11, 172, 251, 0.18))",
      borderRadius: "20px",
      color: "#f8f9fa",
      fontSize: "clamp(0.7rem, 1.8vw, 0.9rem)",
      textShadow: "0 3px 12px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.9)",
      backdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      WebkitBackdropFilter: "blur(35px) saturate(200%) brightness(110%)",
      border: "1px solid rgba(255,255,255,0.4)",
      boxShadow: `
        0 25px 60px rgba(0,0,0,0.7),
        0 12px 30px rgba(0,0,0,0.5),
        0 6px 15px rgba(0,0,0,0.4),
        0 2px 8px rgba(0,0,0,0.3),
        inset 0 3px 0 rgba(255,255,255,0.4),
        inset 0 -3px 0 rgba(0,0,0,0.2),
        inset 2px 0 0 rgba(255,255,255,0.25),
        inset -2px 0 0 rgba(0,0,0,0.15)
      `,
      filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.6))",
      transform: "translateZ(0) perspective(1000px) translateY(-8px)",
      zIndex: 5,
      textAlign: "center",
      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    },
  }), []);

  // Content text styles
  const textStyles = useMemo(() => ({
    featureTitle: {
      fontSize: "clamp(0.8rem, 2vw, 1rem)",
      fontWeight: "bold",
      color: "#f8f9fa",
      marginBottom: "0.5rem"
    },
    featureText: {
      fontSize: "clamp(0.7rem, 1.5vw, 0.85rem)",
      color: "#f8f9fa"
    },
    successTitle: {
      fontSize: "clamp(0.8rem, 2vw, 1rem)",
      fontWeight: "bold",
      color: "#f8f9fa",
      marginBottom: "0.5rem"
    },
    successText: {
      fontSize: "clamp(0.7rem, 1.5vw, 0.85rem)",
      color: "#f8f9fa"
    }
  }), []);

  // Handlers for hover effects
  const handleMouseEnter = useCallback((e) => {
    e.target.style.transform = "translateY(-2px)";
    e.target.style.backgroundColor = "#0090d8";
    e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.3)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.target.style.transform = "translateY(0)";
    e.target.style.backgroundColor = "#01a2f1";
    e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.2)";
  }, []);
  
  const handleMouseDown = useCallback((e) => {
    e.target.style.transform = "translateY(0) scale(0.98)";
    e.target.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.2)";
  }, []);
  
  const handleMouseUp = useCallback((e) => {
    e.target.style.transform = "translateY(-2px) scale(1)";
    e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.3)";
  }, []);

  const handleGoogleButtonMouseEnter = useCallback((e) => {
    e.target.style.transform = "scale(1.02)";
    e.target.style.filter = "drop-shadow(0 6px 12px rgba(0, 0, 0, 0.15))";
  }, []);

  const handleGoogleButtonMouseLeave = useCallback((e) => {
    e.target.style.transform = "scale(1)";
    e.target.style.filter = "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))";
  }, []);

  const handleInputFocus = useCallback((e) => {
    e.target.style.outline = 'none';
    e.target.style.borderColor = '#dadee6';
    e.target.style.boxShadow = '0 0 0 3px rgba(1, 162, 241, 0.1), 0 4px 8px rgba(0, 0, 0, 0.08)';
  }, []);

  const handleInputBlur = useCallback((e) => {
    e.target.style.borderColor = '#dadee6';
    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <style>{`
        body {
          overflow: hidden !important;
          margin: 0 !important;
          padding: 0 !important;
          height: 100vh !important;
          width: 100vw !important;
        }
        
        footer, [class*="footer"], [id*="footer"],
        header, [class*="header"], [id*="header"],
        nav, [class*="navbar"], [class*="nav"] {
          display: none !important;
        }
      `}</style>
      
      <div style={containerStyle}>
        {/* Left Panel - Login Form with Metal Grey Background (swapped) */}
        <div style={leftPanelStyle}>
          <div style={loginContainerStyle}>
            <div style={{ position: "relative", zIndex: 2 }}>
              <div style={logoContainerStyle}>
                <Link to="/">
                  <img
                    src={gigifyLogo}
                    alt="GIGiFY"
                    style={{
                      width: "100%",
                      height: "auto",
                      transition: "transform 0.3s ease"
                    }}
                    onMouseEnter={handleGoogleButtonMouseEnter}
                    onMouseLeave={handleGoogleButtonMouseLeave}
                  />
                </Link>
              </div>
              

              <p style={{
                marginTop: isMobile ? (isTrueMobile ? "1rem" : "1.5rem") : scaleRem(2), // Added top margin
                marginBottom: isMobile ? (isTrueMobile ? "1rem" : "1.5rem") : scaleRem(2), // Increased bottom margin
                fontSize: isMobile ? (isTrueMobile ? "1rem" : "1.2rem") : scaleRem(1.8), // Increased font size for prominence
                color: "#f8f9fa",
                textAlign: "center"
              }}>
                Welcome back to GIGifY
              </p>
              
              {error && (
                <div style={errorMessageStyle}>
                  {error}
                </div>
              )}
              
              {success && (
                <div style={{
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  color: "#00A86B",
                  borderRadius: "12px",
                  padding: "clamp(0.75rem, 2vw, 1.25rem)",
                  marginBottom: "1.5rem",
                  textAlign: "center",
                  border: "1px solid rgba(76, 175, 80, 0.2)",
                  animation: "fadeIn 0.3s ease-in-out",
                  fontSize: "clamp(0.9rem, 1.2vw, 1rem)"
                }}>
                  {success}
                </div>
              )}
              
              <form
                onSubmit={handleLogin}
                style={formStyle}
                autoComplete="off" 
              >
                <input
                  type="text"
                  style={{display: 'none'}}
                  name="prevent_autofill"
                  id="prevent_autofill_login" 
                  defaultValue=""
                />
                <input
                  type="email"
                  name="login-email"
                  placeholder="Email"
                  value={loginEmail}
                  onChange={handleInputChange(setLoginEmail)}
                 style={inputStyle}
                 required
                 autoComplete="new-password"
                 onFocus={handleInputFocus}
                 onBlur={handleInputBlur}
               />
               <div style={{ position: 'relative', width: '100%' }}>
                 <input
                   type={showPassword ? "text" : "password"}
                   name="login-password"
                   placeholder="Password"
                   value={loginPassword}
                   onChange={handleInputChange(setLoginPassword)}
                   style={{
                     ...inputStyle,
                     paddingRight: "3rem"
                   }}
                   required
                   autoComplete="new-password"
                   onFocus={handleInputFocus}
                   onBlur={handleInputBlur}
                 />
                 <button
                   type="button"
                   onClick={() => setShowPassword(!showPassword)}
                   style={{
                     position: 'absolute',
                     right: '12px',
                     top: '50%',
                     transform: 'translateY(-50%)',
                     background: 'transparent',
                     border: 'none',
                     cursor: 'pointer',
                     padding: '8px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     color: '#01a2f1',
                     transition: 'color 0.2s ease',
                     minWidth: '44px',
                     minHeight: '44px'
                   }}
                   onMouseEnter={(e) => e.currentTarget.style.color = '#01a2f1'}
                   onMouseLeave={(e) => e.currentTarget.style.color = '#01a2f1'}
                   aria-label={showPassword ? "Hide password" : "Show password"}
                 >
                   {showPassword ? <EyeOff size={25} /> : <Eye size={25} />}
                 </button>
               </div>
               <button
                 type="submit"
                 style={submitButtonStyle}
                 onMouseEnter={handleMouseEnter}
                 onMouseLeave={handleMouseLeave}
                 onMouseDown={handleMouseDown}
                 onMouseUp={handleMouseUp}
               >
                 Login
               </button>
             </form>

             <div id="googleSignInButton" style={{ display: 'none' }}></div>
             
             {/* "or get started with" divider */}
             <div style={dividerStyle}>
               <div style={dividerLineStyle}></div>
               <span>or</span>
               <div style={dividerLineStyle}></div>
             </div>
             
             <img
               src={googleButtonImageSI}
               alt="Google Login"
               onClick={handleGoogleAuth}
               style={googleButtonStyle}
               onMouseEnter={handleGoogleButtonMouseEnter}
               onMouseLeave={handleGoogleButtonMouseLeave}
             />
             
             {/* Forgot Password Link */}
             <p style={{ 
               marginTop: isMobile ? (isTrueMobile ? "0.2rem" : "0.25rem") : scaleRem(0.6), // Smaller for true mobile
               marginBottom: isMobile ? (isTrueMobile ? "0.2rem" : "0.25rem") : scaleRem(0.5), // Smaller for true mobile
               fontSize: isMobile ? (isTrueMobile ? "0.65rem" : "0.75rem") : scaleRem(1.105), // Smaller for true mobile
               color: "#f8f9fa",
               textAlign: "center"
             }}>
               <Link 
                 to="/forgot-password" 
                 style={{ 
                   color: "#01a2f1", 
                   textDecoration: "none",
                   fontWeight: "500"
                 }}
                 onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                 onMouseLeave={(e) => e.target.style.textDecoration = "none"}
               >
                 Forgot your password?
               </Link>
             </p>

             <p style={{ 
               marginTop: isMobile ? (isTrueMobile ? "0.2rem" : "0.25rem") : scaleRem(0.6), // Smaller for true mobile
               marginBottom: isMobile ? (isTrueMobile ? "0.2rem" : "0.25rem") : scaleRem(0.5), // Smaller for true mobile
               fontSize: isMobile ? (isTrueMobile ? "0.65rem" : "0.75rem") : scaleRem(1.105), // Smaller for true mobile
               color: "#f8f9fa",
               textAlign: "center"
             }}>
               Don't have an account?{" "}
               <Link 
                 to="/register" 
                 style={{ 
                   color: "#01a2f1", 
                   textDecoration: "none",
                   fontWeight: "500"
                 }}
                 onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                 onMouseLeave={(e) => e.target.style.textDecoration = "none"}
               >
                 Register here
               </Link>
             </p>
           </div>
         </div>
       </div>
       
       {/* Right Panel - Hero Image with Signup Background (Hidden on Mobile) */}
       {!isMobile && (
         <div style={rightPanelStyle}>
           {/* Main heading at the top */}
           <div style={marketingContainerStyles.mainHeading}>
             {/* Heading removed as requested */}
           </div>
       
           
           {/* Hero Image */}
           <img
             src={heroImage}
             alt="Hero"
             style={heroImageStyle}
             onMouseEnter={(e) => {
               e.currentTarget.style.filter = `
                 drop-shadow(0 ${scaleValue(20)} ${scaleValue(40)} rgba(0,0,0,0.5))
                 drop-shadow(0 ${scaleValue(40)} ${scaleValue(80)} rgba(0,0,0,0.3))
                 contrast(1.15)
                 saturate(1.25)
                 brightness(1.08)
                 sharpen(0.4)
               `;
               e.currentTarget.style.transform = "translateZ(0) scale(1.02) translateY(-5px)";
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.filter = `
                 drop-shadow(0 ${scaleValue(15)} ${scaleValue(30)} rgba(0,0,0,0.4))
                 drop-shadow(0 ${scaleValue(30)} ${scaleValue(60)} rgba(0,0,0,0.2))
                 contrast(1.1)
                 saturate(1.15)
                 brightness(1.05)
                 sharpen(0.3)
               `;
               e.currentTarget.style.transform = "translateZ(0) scale(1) translateY(0)";
             }}
          />
          
          {/* Dashboard container */}
          <div
            style={marketingContainerStyles.dashboardContainer}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-15px) scale(1.02)";
              e.currentTarget.style.boxShadow = `
                0 35px 80px rgba(0,0,0,0.8),
                0 18px 40px rgba(0,0,0,0.6),
                0 8px 20px rgba(0,0,0,0.5),
                0 3px 10px rgba(0,0,0,0.4),
                inset 0 3px 0 rgba(255,255,255,0.5),
                inset 0 -3px 0 rgba(0,0,0,0.2),
                inset 2px 0 0 rgba(255,255,255,0.3),
                inset -2px 0 0 rgba(0,0,0,0.15)
              `;
              e.currentTarget.style.filter = "drop-shadow(0 20px 45px rgba(0,0,0,0.7))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-8px)";
              e.currentTarget.style.boxShadow = `
                0 25px 60px rgba(0,0,0,0.7),
                0 12px 30px rgba(0,0,0,0.5),
                0 6px 15px rgba(0,0,0,0.4),
                0 2px 8px rgba(0,0,0,0.3),
                inset 0 3px 0 rgba(255,255,255,0.4),
                inset 0 -3px 0 rgba(0,0,0,0.2),
                inset 2px 0 0 rgba(255,255,255,0.25),
                inset -2px 0 0 rgba(0,0,0,0.15)
              `;
              e.currentTarget.style.filter = "drop-shadow(0 15px 35px rgba(0,0,0,0.6))";
            }}
          >
            <strong>Your Dashboard</strong> is ready with all your booking management tools,
            analytics, and profile updates in one place -
            <span style={{color: "#01a2f1", fontWeight: "bold"}}> your command centre awaits!</span>
          </div>

          {/* Feature container */}
          <div
            style={marketingContainerStyles.featureContainer}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-15px) scale(1.02)";
              e.currentTarget.style.boxShadow = `
                0 35px 80px rgba(0,0,0,0.8),
                0 18px 40px rgba(0,0,0,0.6),
                0 8px 20px rgba(0,0,0,0.5),
                0 3px 10px rgba(0,0,0,0.4),
                inset 0 3px 0 rgba(255,255,255,0.5),
                inset 0 -3px 0 rgba(0,0,0,0.2),
                inset 2px 0 0 rgba(255,255,255,0.3),
                inset -2px 0 0 rgba(0,0,0,0.15)
              `;
              e.currentTarget.style.filter = "drop-shadow(0 20px 45px rgba(0,0,0,0.7))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-8px)";
              e.currentTarget.style.boxShadow = `
                0 25px 60px rgba(0,0,0,0.7),
                0 12px 30px rgba(0,0,0,0.5),
                0 6px 15px rgba(0,0,0,0.4),
                0 2px 8px rgba(0,0,0,0.3),
                inset 0 3px 0 rgba(255,255,255,0.4),
                inset 0 -3px 0 rgba(0,0,0,0.2),
                inset 2px 0 0 rgba(255,255,255,0.25),
                inset -2px 0 0 rgba(0,0,0,0.15)
              `;
              e.currentTarget.style.filter = "drop-shadow(0 15px 35px rgba(0,0,0,0.6))";
            }}
          >
            <div style={textStyles.featureTitle}>New Features</div>
            <div style={textStyles.featureText}>
              Check out the latest{" "}
              <span style={{color: "#f8f9fa", fontWeight: "bold"}}>booking tools</span>{" "}
              and enhanced profile features we've added just for you.
            </div>
          </div>

          {/* Success container */}
          <div
            style={marketingContainerStyles.successContainer}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-15px) scale(1.02)";
              e.currentTarget.style.boxShadow = `
                0 35px 80px rgba(0,0,0,0.8),
                0 18px 40px rgba(0,0,0,0.6),
                0 8px 20px rgba(0,0,0,0.5),
                0 3px 10px rgba(0,0,0,0.4),
                inset 0 3px 0 rgba(255,255,255,0.5),
                inset 0 -3px 0 rgba(0,0,0,0.2),
                inset 2px 0 0 rgba(255,255,255,0.3),
                inset -2px 0 0 rgba(0,0,0,0.15)
              `;
              e.currentTarget.style.filter = "drop-shadow(0 20px 45px rgba(0,0,0,0.7))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateZ(0) perspective(1000px) translateY(-8px)";
              e.currentTarget.style.boxShadow = `
                0 25px 60px rgba(0,0,0,0.7),
                0 12px 30px rgba(0,0,0,0.5),
                0 6px 15px rgba(0,0,0,0.4),
                0 2px 8px rgba(0,0,0,0.3),
                inset 0 3px 0 rgba(255,255,255,0.4),
                inset 0 -3px 0 rgba(0,0,0,0.2),
                inset 2px 0 0 rgba(255,255,255,0.25),
                inset -2px 0 0 rgba(0,0,0,0.15)
              `;
              e.currentTarget.style.filter = "drop-shadow(0 15px 35px rgba(0,0,0,0.6))";
            }}
          >
            <div style={textStyles.successTitle}>Your Success</div>
            <div style={textStyles.successText}>
              Continue building your{" "}
              <span style={{color: "#f8f9fa", fontWeight: "bold"}}>reputation</span>{" "}
              and growing your network in the GIGiFY community.
            </div>
          </div>
        </div>
      )}
    </div>
  </ErrorBoundary>
);
};

export default Login;